import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  BranchDeleteImpact,
  SettingsService,
} from '../../core/services/settings.service';
import { Branch, Location } from '../../core/models/settings.models';
import { AuthService } from '../auth/auth.service';

@Component({
  selector: 'app-branches',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './branches.component.html',
  styleUrls: ['../../settings-shared.css', './branches.component.css'],
})
export class BranchesComponent implements OnInit {
  private readonly settings = inject(SettingsService);
  protected readonly auth = inject(AuthService);

  protected readonly showModal = signal(false);
  protected readonly editingId = signal<string | null>(null);
  protected readonly isEditing = computed(() => this.editingId() !== null);
  protected readonly showToast = signal(false);
  protected readonly toastMessage = signal('');
  protected readonly toastIsError = signal(false);

  protected readonly showDeleteModal = signal(false);
  protected readonly deleteLoading = signal(false);
  protected readonly deleteSubmitting = signal(false);
  protected readonly deleteTarget = signal<Branch | null>(null);
  protected readonly deleteImpact = signal<BranchDeleteImpact | null>(null);
  protected readonly deleteError = signal('');
  protected readonly targetBranchId = signal('');
  protected readonly targetStoreLocationId = signal('');

  protected branchForm: Omit<Branch, 'id'> = {
    code: '',
    name: '',
    city: '',
    status: 'active',
  };

  protected readonly branches = this.settings.companyBranches;
  protected readonly locations = this.settings.companyLocations;

  protected readonly destinationBranches = computed(() => {
    const target = this.deleteTarget();
    if (!target) {
      return [] as Branch[];
    }
    return this.branches().filter(
      (b) => b.id !== target.id && b.status === 'active',
    );
  });

  protected readonly destinationLocations = computed(() => {
    const branchId = this.targetBranchId();
    if (!branchId) {
      return [] as Location[];
    }
    return this.locations().filter(
      (loc) => loc.branchId === branchId && loc.status === 'active',
    );
  });

  ngOnInit(): void {
    this.settings.refreshFromApi();
  }

  openModal(): void {
    this.editingId.set(null);
    this.resetForm();
    this.showModal.set(true);
  }

  openEdit(row: Branch): void {
    this.editingId.set(row.id);
    this.branchForm = {
      code: row.code,
      name: row.name,
      city: row.city,
      status: row.status,
    };
    this.showModal.set(true);
  }

  closeModal(): void {
    this.showModal.set(false);
    this.editingId.set(null);
  }

  save(): void {
    const editingId = this.editingId();

    if (!this.branchForm.name?.trim()) {
      this.showNotice('Name is required', true);
      return;
    }

    if (editingId) {
      this.settings.updateBranch(editingId, this.branchForm).subscribe((result) => {
        this.showNotice(result.message, !result.success);
        if (result.success) {
          this.closeModal();
        }
      });
      return;
    }

    this.settings.addBranch(this.branchForm).subscribe((result) => {
      this.showNotice(result.message, !result.success);
      if (result.success) {
        this.closeModal();
      }
    });
  }

  deleteRow(row: Branch): void {
    if (!this.auth.hasPermission('edit_settings')) {
      this.showNotice('You do not have permission to delete branches.', true);
      return;
    }
    this.deleteTarget.set(row);
    this.deleteImpact.set(null);
    this.deleteError.set('');
    this.targetBranchId.set('');
    this.targetStoreLocationId.set('');
    this.showDeleteModal.set(true);
    this.deleteLoading.set(true);

    this.settings.getBranchDeleteImpact(row.id).subscribe((impact) => {
      this.deleteLoading.set(false);
      if (!impact) {
        this.deleteError.set('Could not check items in this branch. Try again.');
        return;
      }
      this.deleteImpact.set(impact);

      if (impact.isHomeBranch) {
        this.deleteError.set('You cannot delete your home branch.');
        return;
      }
      if (impact.userCount > 0) {
        this.deleteError.set(
          `This branch still has ${impact.userCount} user(s) assigned. Reassign them before deleting.`,
        );
      }

      const destinations = this.destinationBranches();
      if (impact.requiresReassignment && destinations.length === 1) {
        this.targetBranchId.set(destinations[0].id);
        this.onTargetBranchChange();
      }
    });
  }

  onTargetBranchChange(): void {
    this.targetStoreLocationId.set('');
    const locs = this.destinationLocations();
    if (locs.length === 1) {
      this.targetStoreLocationId.set(locs[0].id);
    }
  }

  closeDeleteModal(): void {
    if (this.deleteSubmitting()) {
      return;
    }
    this.showDeleteModal.set(false);
    this.deleteTarget.set(null);
    this.deleteImpact.set(null);
    this.deleteError.set('');
    this.targetBranchId.set('');
    this.targetStoreLocationId.set('');
  }

  confirmDelete(): void {
    const target = this.deleteTarget();
    const impact = this.deleteImpact();
    if (!target || !impact || this.deleteSubmitting()) {
      return;
    }

    if (impact.isHomeBranch) {
      this.deleteError.set('You cannot delete your home branch.');
      return;
    }
    if (impact.userCount > 0) {
      this.deleteError.set(
        `This branch still has ${impact.userCount} user(s) assigned. Reassign them before deleting.`,
      );
      return;
    }

    if (impact.requiresReassignment) {
      const branchId = Number(this.targetBranchId());
      const locationId = Number(this.targetStoreLocationId());
      if (!branchId || !locationId) {
        this.deleteError.set(
          'Select a destination branch and store location for the items.',
        );
        return;
      }
    }

    this.deleteError.set('');
    this.deleteSubmitting.set(true);

    const options = impact.requiresReassignment
      ? {
          targetBranchId: Number(this.targetBranchId()),
          targetStoreLocationId: Number(this.targetStoreLocationId()),
        }
      : undefined;

    this.settings.deleteBranch(target.id, options).subscribe((result) => {
      this.deleteSubmitting.set(false);
      this.showNotice(result.message, !result.success);
      if (result.success) {
        this.closeDeleteModal();
      } else {
        this.deleteError.set(result.message);
      }
    });
  }

  private resetForm(): void {
    this.branchForm = {
      code: '',
      name: '',
      city: '',
      status: 'active',
    };
  }

  private showNotice(message: string, isError = false): void {
    this.toastIsError.set(isError);
    this.toastMessage.set(message);
    this.showToast.set(true);
    setTimeout(() => this.showToast.set(false), 4000);
  }
}
